import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Divider,
} from "antd";
import { SettingOutlined, SaveOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import {
  getInvoiceSettings,
  updateInvoiceSettings,
} from "../../services/invoiceSettingsService";

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;

const InvoiceSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getInvoiceSettings();
      form.setFieldsValue(response.data);
    } catch (error) {
      console.error("Error fetching invoice settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await updateInvoiceSettings(values);
      toast.success("Fatura ayarları kaydedildi");
    } catch (error) {
      if (!error.errorFields) {
        console.error("Error saving invoice settings:", error);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 24 }}
          >
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <SettingOutlined style={{ color: "#1890ff", marginRight: 12 }} />
                Fatura Ayarları
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSave}
              >
                Kaydet
              </Button>
            </Col>
          </Row>
        </motion.div>

        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                >
                  <Card
                    title="Şirket Bilgileri"
                    style={{ borderRadius: 8 }}
                  >
                    <Form.Item
                      name="companyName"
                      label="Şirket Adı"
                      rules={[
                        { required: true, message: "Şirket adı gerekli" },
                      ]}
                    >
                      <Input placeholder="Şirket adı" />
                    </Form.Item>
                    <Form.Item name="address" label="Adres">
                      <TextArea rows={2} placeholder="Şirket adresi" />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="phone" label="Telefon">
                          <Input placeholder="Telefon" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="email" label="E-posta">
                          <Input placeholder="E-posta" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="taxNumber" label="Vergi Numarası">
                          <Input placeholder="Vergi numarası" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="taxOffice" label="Vergi Dairesi">
                          <Input placeholder="Vergi dairesi" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="postalCode" label="Posta Kodu">
                          <Input placeholder="Posta kodu" maxLength={10} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="mersisNo" label="MERSIS No">
                          <Input placeholder="MERSIS numarası" maxLength={20} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="tradeRegisterNo" label="Ticaret Sicil No">
                          <Input placeholder="Ticaret sicil no" maxLength={30} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </motion.div>
              </Col>

              <Col xs={24} lg={12}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.4 }}
                >
                  <Card
                    title="Fatura Ayarları"
                    style={{ borderRadius: 8 }}
                  >
                    <Form.Item name="invoicePrefix" label="Fatura Ön Eki">
                      <Input placeholder="Örn: INV" />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="defaultTaxRate"
                          label="Varsayılan KDV Oranı (%)"
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            style={{ width: "100%" }}
                            placeholder="20"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="defaultDueDays"
                          label="Varsayılan Vade (gün)"
                        >
                          <InputNumber
                            min={0}
                            style={{ width: "100%" }}
                            placeholder="30"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider>Banka Bilgileri</Divider>

                    <Form.Item name="bankName" label="Banka Adı">
                      <Input placeholder="Banka adı" />
                    </Form.Item>
                    <Form.Item name="iban" label="IBAN">
                      <Input placeholder="TR..." />
                    </Form.Item>
                  </Card>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  style={{ marginTop: 16 }}
                >
                  <Card
                    title="Fatura Alt Notu"
                    style={{ borderRadius: 8 }}
                  >
                    <Form.Item name="footerNote" label="Alt Not">
                      <TextArea
                        rows={4}
                        placeholder="Faturanın alt kısmında görünecek not"
                      />
                    </Form.Item>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Content>
    </Layout>
  );
};

export default InvoiceSettingsPage;
